import { useState } from 'react'
import { dictionaries, translate, type Lang } from './i18n'
import type { Timeline, TimelineEntry , Denial} from './api'
import { DataNote } from './DataNote'
import { fmtDate, fmtTime } from './time'

/** Las familias por las que se filtra. Vacío es «todo». */
const FAMILIAS = ['alert', 'incident', 'unit', 'reoptimization', 'plan', 'handover']

/**
 * La bitácora operacional del Centro de Mando (§10.1).
 *
 * **No es la bitácora de Administración.** Son dos públicos y preguntan cosas distintas: el
 * auditor pregunta meses después «quién autorizó esto», y le sirve una línea por acto de
 * autoridad; el supervisor pregunta *durante* el turno «qué pasó mientras no estaba», y le sirve
 * todo lo que ocurrió, en orden, incluida la unidad que intentó marcar entrada y no pudo.
 *
 * Es continua y no por turno, como el libro de guardia de verdad: el relevo aparece dentro de
 * ella como un renglón más.
 */
export function TimelinePanel({
  lang, timeline, hours, onHours, busy, loaded, problem,
}: {
  lang: Lang
  timeline: Timeline | null
  hours: number
  onHours: (hours: number) => void
  busy: boolean
  loaded: boolean
  problem: Denial | null
}) {
  const [familia, setFamilia] = useState('')
  const [unidad, setUnidad] = useState('')

  // En la hora de la agencia o la de quien mira, con la zona escrita (H-01).
  const hora = (iso: string) => fmtTime(iso, lang)
  const dia = (iso: string) => fmtDate(iso, lang, { day: '2-digit', month: 'short' })

  const filtradas = (timeline?.entries ?? []).filter((e) =>
    (familia === '' || e.kind.startsWith(`${familia}.`)) &&
    (unidad === '' || e.callSign.toLowerCase().includes(unidad.toLowerCase())))

  /**
   * La frase del renglón, en el idioma de quien mira.
   *
   * Si esta versión no conoce el código, se muestra el código: es feo y es honesto, y deja ver
   * que llegó algo nuevo en vez de esconder el renglón.
   */
  const frase = (e: TimelineEntry) => translate(lang, `log.${e.kind}`, localizados(e))

  /**
   * El tipo y la gravedad de un desvío llegan como códigos (LateArrival, Warning): se dicen en el
   * idioma de quien mira. Antes se leía «Desvío detectado: LateArrival (Warning)».
   */
  const localizados = (e: TimelineEntry): Record<string, string> => {
    const conocido = (clave: string, crudo: string) =>
      clave in dictionaries[lang] ? translate(lang, clave) : crudo
    const args: Record<string, string> = { ...(e.args ?? {}) }
    if (e.kind.startsWith('alert.') && args.kind) args.kind = conocido(`kind.${args.kind}`, args.kind)
    if (args.severity) args.severity = conocido(`severity.${args.severity}`, args.severity)
    // Por qué un teléfono borró registros sin enviarlos (H-71).
    if (e.kind === 'unit.datalost' && args.reason) args.reason = conocido(`loss.${args.reason}`, args.reason)
    // Fuera de servicio o de vuelta: por radio o desde el teléfono (H-62).
    if (args.source) args.source = conocido(`source.${args.source}`, args.source)
    return args
  }

  return (
    <section className="panel">
      <h2>{translate(lang, 'log.title')}</h2>
      <p className="notice">{translate(lang, 'log.help')}</p>

      <div className="actions">
        <select value={hours} disabled={busy} onChange={(e) => onHours(Number(e.target.value))}
          aria-label={translate(lang, 'log.window')}>
          {[2, 8, 12, 24, 72].map((h) => (
            <option key={h} value={h}>{translate(lang, 'log.lastHours', [h])}</option>
          ))}
        </select>
        <select value={familia} onChange={(e) => setFamilia(e.target.value)}
          aria-label={translate(lang, 'log.filter')}>
          <option value="">{translate(lang, 'log.all')}</option>
          {FAMILIAS.map((f) => (
            <option key={f} value={f}>{translate(lang, `log.family.${f}`)}</option>
          ))}
        </select>
        <input
          value={unidad} onChange={(e) => setUnidad(e.target.value)}
          placeholder={translate(lang, 'log.unit')}
          aria-label={translate(lang, 'log.unit')}
          style={{ maxWidth: 140 }}
        />
      </div>

      {/* Cuántos se muestran de cuántos hay: una lista recortada que no avisa que está
          recortada miente sobre el tamaño de lo que pasó. */}
      {timeline !== null && (
        <p className="notice">
          {translate(lang, 'log.showing', [filtradas.length, timeline.shown])}
        </p>
      )}

      {/* Con el total, no con lo filtrado: «no hubo movimientos» es una afirmación sobre el
          turno, y filtrar a cero no lo es. */}
      <DataNote
        lang={lang} loaded={loaded} problem={problem}
        count={timeline?.shown ?? 0} empty="log.quiet"
      />
      {(timeline?.shown ?? 0) > 0 && filtradas.length === 0 && (
        <p className="notice">{translate(lang, 'log.noneMatch')}</p>
      )}

      {filtradas.map((e, i) => (
        <div key={`${e.at}-${e.kind}-${i}`} className={`card ${e.kind === 'unit.rejected' ? 'warning' : ''}`}>
          <div className="row-line">
            <strong className="grow">{frase(e)}</strong>
            <span className="meta">{dia(e.at)} {hora(e.at)}</span>
          </div>
          <div className="meta">
            {e.callSign !== '' && `${e.callSign} · `}
            {/* Sin actor lo detectó el sistema, y decirlo distingue lo que alguien decidió de
                lo que el sistema notó solo. */}
            {e.actor !== '' ? translate(lang, 'log.by', [actorName(e.actor)]) : translate(lang, 'log.detected')}
          </div>
          {e.note !== '' && <p className="notice">{e.note}</p>}
        </div>
      ))}
    </section>
  )
}

/**
 * Quién lo hizo, como lo lee una persona. Algunos registros guardan el origen como prefijo
 * (`officer:`, `command:`) y la bitácora mostraba «lo hizo officer:FWSYN-CENTRAL-E001» junto a
 * «lo hizo FWSYN-CENTRAL-E001» para la misma persona.
 */
export function actorName(actor: string): string {
  return actor.replace(/^(officer|command|system):/, '')
}
